'use client';

import { type ReactElement } from 'react';
import { cleanPhoneNumber, cn, formatPhoneNumberForDesign } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import { IconNoAvailableDate, IconCloseAlt } from '../icon';
import getCtaStyles from '../button/get-cta-styles';

export type Props = {
	headingLabel?: string;
	bodyLabel?: string;
	chatLabel?: string;
	chatHref?: string;
	phoneNumber?: string;
	onClose?: () => void;
	className?: string;
};

export default function NonBookableWidget({
	headingLabel = 'Contact our concierge to book',
	bodyLabel = 'Let us help you arrange this experience',
	chatLabel = 'Chat with us',
	chatHref,
	phoneNumber,
	onClose,
	className = '',
}: Props): ReactElement | null {
	if (!chatHref && !phoneNumber) return null;

	const isBottomSheet = Boolean(onClose);
	const sanitizedPhone = phoneNumber && cleanPhoneNumber(phoneNumber);
	const formattedPhone = phoneNumber && formatPhoneNumberForDesign(phoneNumber);

	return (
		<div
			data-booking-widget
			className={cn(
				'bg-surface-raised',
				'border border-border-subtle',
				'flex flex-col',
				'shadow-[0_5px_7.5px_rgba(3,3,3,0.12)]',
				isBottomSheet ? 'rounded-t-[20px]' : 'rounded-[20px]',
				className
			)}
		>
			{isBottomSheet && (
				<div className="flex h-[74px] items-center justify-end rounded-t-[20px] bg-surface-default px-200">
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="flex items-center justify-center"
					>
						<IconCloseAlt width={24} height={24} />
					</button>
				</div>
			)}
			<div
				className={cn(
					'flex flex-col',
					isBottomSheet ? 'items-center gap-300 px-250 pb-400' : 'items-start gap-300 p-300'
				)}
			>
				<div
					className={cn(
						'flex w-full flex-col gap-200',
						isBottomSheet ? 'items-center' : 'items-start'
					)}
				>
					<div
						className={cn(
							'flex w-full',
							isBottomSheet ? 'flex-col items-center gap-200' : 'flex-row items-center gap-100'
						)}
					>
						<span className="shrink-0">
							<IconNoAvailableDate
								width={isBottomSheet ? 40 : 30}
								height={isBottomSheet ? 40 : 30}
							/>
						</span>
						<p className={cn('text-text-heading', isBottomSheet && 'text-center')}>
							<span className="text-subtitle1">
								<BevSafeText>{headingLabel}</BevSafeText>
							</span>
						</p>
					</div>

					<p className={cn('text-text-primary', isBottomSheet ? 'text-center' : 'text-left')}>
						<span className="text-body2">
							<BevSafeText>{bodyLabel}</BevSafeText>
						</span>
					</p>
				</div>

				<div className="flex w-full flex-col gap-100">
					{chatHref && (
						<a
							href={chatHref}
							className={getCtaStyles({ intent: 'filled', size: 'small', className: 'w-full' })}
						>
							{chatLabel}
						</a>
					)}
					{phoneNumber && (
						<a
							href={`tel:${sanitizedPhone}`}
							aria-label={`Call ${formattedPhone}`}
							className={getCtaStyles({ intent: 'outline', size: 'small', className: 'w-full' })}
						>
							{formattedPhone}
						</a>
					)}
				</div>
			</div>
		</div>
	);
}
